/**
 * useVariantUnlocks — Shared hook for Djay variant unlock conditions.
 *
 * DDD co-locatie: mascot domain.
 * Single source of truth for which variants are unlocked, used by
 * FloatingDjay, Sidebar, MobileNav, and CollectionPage.
 */

import { useMemo } from 'react'
import { DjayVariant } from './DjayAvatar'
import { useAppStore } from '../app/hooks/useAppStore'
import { MODULES } from '../content'

export type MascotRarity =
  | 'common'
  | 'uncommon'
  | 'rare'
  | 'legendary'
  | 'secret'

export interface VariantProgress {
  current: number
  max: number
  label: string
}

export interface VariantUnlockInfo {
  id: DjayVariant
  name: string
  unlocked: boolean
  /** Human-readable unlock condition (Dutch) */
  condition: string
  rarity: MascotRarity
  description: string
  progress: VariantProgress
}

interface UnlockContext {
  totalQuestionsAnswered: number
  masteredCount: number
  consecutiveFullWeeks: number
  level: number
  xp: number
  totalModules: number
  hasDiscoveredLongPress: boolean
}

interface VariantDefinition {
  id: DjayVariant
  name: string
  /** Name shown when secret is not yet discovered */
  hiddenName?: string
  condition: string
  rarity: MascotRarity
  description: string
  /** Description shown when secret is not yet discovered */
  hiddenDescription?: string
  check: (ctx: UnlockContext) => boolean
  getProgress: (ctx: UnlockContext) => VariantProgress
}

const VARIANT_DEFINITIONS: VariantDefinition[] = [
  {
    id: 'default',
    name: 'Standaard Djay',
    condition: 'Altijd beschikbaar',
    rarity: 'common',
    description: 'Altijd beschikbaar',
    check: () => true,
    getProgress: () => ({ current: 1, max: 1, label: 'Ontgrendeld' }),
  },
  {
    id: 'pixel',
    name: 'DJ Pixel',
    condition: '100 vragen beantwoord',
    rarity: 'uncommon',
    description: 'Honderdtal bereikt',
    check: (ctx) => ctx.totalQuestionsAnswered >= 100,
    getProgress: (ctx) => ({
      current: Math.min(ctx.totalQuestionsAnswered, 100),
      max: 100,
      label: 'vragen',
    }),
  },
  {
    id: 'blaze',
    name: 'DJ Blaze',
    condition: '3 weken streak',
    rarity: 'rare',
    description: '3 volle weken achter elkaar',
    check: (ctx) => ctx.consecutiveFullWeeks >= 3,
    getProgress: (ctx) => ({
      current: Math.min(ctx.consecutiveFullWeeks, 3),
      max: 3,
      label: 'weken',
    }),
  },
  {
    id: 'frost',
    name: 'DJ Frost',
    condition: '3 vakken beheerst',
    rarity: 'rare',
    description: '3 vakken afgerond',
    check: (ctx) => ctx.masteredCount >= 3,
    getProgress: (ctx) => ({
      current: Math.min(ctx.masteredCount, 3),
      max: 3,
      label: 'vakken',
    }),
  },
  {
    id: 'royal',
    name: 'DJ Royal',
    condition: 'Alle vakken beheerst',
    rarity: 'legendary',
    description: 'Alle vakken afgerond',
    check: (ctx) => ctx.masteredCount >= ctx.totalModules,
    getProgress: (ctx) => ({
      current: Math.min(ctx.masteredCount, ctx.totalModules),
      max: ctx.totalModules,
      label: 'vakken',
    }),
  },
  {
    id: 'cosmic',
    name: 'DJ Cosmic',
    condition: 'Level 15 bereikt',
    rarity: 'legendary',
    description: 'Level 15 bereikt',
    check: (ctx) => ctx.level >= 15,
    getProgress: (ctx) => ({
      current: Math.min(ctx.level, 15),
      max: 15,
      label: 'levels',
    }),
  },
  {
    id: 'shadow',
    name: 'DJ Shadow',
    condition: '30.000 XP verzameld',
    rarity: 'legendary',
    description: '30.000 XP behaald',
    check: (ctx) => ctx.xp >= 30000,
    getProgress: (ctx) => ({
      current: Math.min(ctx.xp, 30000),
      max: 30000,
      label: 'XP',
    }),
  },
  {
    id: 'goud',
    name: 'DJ Goud',
    hiddenName: '???',
    condition: 'Geheim ontdekt',
    rarity: 'secret',
    description: 'Geheim ontdekt!',
    hiddenDescription: 'Geheim! Ontdek het zelf...',
    check: (ctx) => ctx.hasDiscoveredLongPress,
    getProgress: (ctx) => ({
      current: ctx.hasDiscoveredLongPress ? 1 : 0,
      max: 1,
      label: 'Geheim',
    }),
  },
]

/**
 * Returns all variant unlock info based on current learner state.
 * Pass `hasDiscoveredLongPress` from the caller since it requires
 * an async dataLayer lookup that the caller manages.
 */
export function useVariantUnlocks(hasDiscoveredLongPress: boolean): {
  variants: VariantUnlockInfo[]
  unlockedCount: number
} {
  const { state, getModuleMasteryForGroup, getAnsweredQuestions } =
    useAppStore()

  return useMemo(() => {
    let totalQs = 0
    let mastered = 0
    MODULES.forEach((m) => {
      totalQs += getAnsweredQuestions(m.id).length
      if (getModuleMasteryForGroup(m.id) >= 85) mastered++
    })

    const ctx: UnlockContext = {
      totalQuestionsAnswered: totalQs,
      masteredCount: mastered,
      consecutiveFullWeeks: state?.dailyStreak?.consecutiveFullWeeks || 0,
      level: state?.stats?.level || 1,
      xp: state?.stats?.xp || 0,
      totalModules: MODULES.length,
      hasDiscoveredLongPress,
    }

    const variants: VariantUnlockInfo[] = VARIANT_DEFINITIONS.map((def) => {
      const isUnlocked = def.check(ctx)
      return {
        id: def.id,
        name: def.hiddenName && !isUnlocked ? def.hiddenName : def.name,
        unlocked: isUnlocked,
        condition: def.condition,
        rarity: def.rarity,
        description:
          def.hiddenDescription && !isUnlocked
            ? def.hiddenDescription
            : def.description,
        progress: def.getProgress(ctx),
      }
    })

    const unlockedCount = variants.filter((v) => v.unlocked).length

    return { variants, unlockedCount }
  }, [
    state?.stats?.level,
    state?.stats?.xp,
    state?.dailyStreak?.consecutiveFullWeeks,
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    hasDiscoveredLongPress,
  ])
}

/** Total number of variants available */
export const TOTAL_VARIANTS = VARIANT_DEFINITIONS.length
